/**
 * Vessel feature-icon catalogue.
 *
 * Single source of truth for every icon that can decorate a key feature on a
 * vessel flyer. Anything that needs to render or pick a feature icon (the
 * flyer renderer, the editor's icon picker, future PDF exports) reads from
 * this module — so adding a new icon means touching exactly one file.
 *
 * Each catalogue entry is:
 *   { key:      stable kebab-case id persisted on the feature (`feature.icon_key`),
 *     label:    human-readable name shown in the picker tooltip,
 *     Icon:     React component that renders the icon at the size given by
 *               the consumer (must accept ``className`` + ``strokeWidth``),
 *     keywords: optional regex used by ``pickFeatureIcons()`` when the
 *               admin has not chosen a manual override yet.
 *   }
 *
 * The catalogue is also the order shown in the picker grid — keep the most-
 * used icons first (vessel-specific custom SVGs at the top, generic lucide
 * icons after).
 */
import {
  Anchor,
  Boxes,
  Building2,
  Cog,
  Columns3,
  Compass,
  DollarSign,
  DoorOpen,
  Fuel,
  Gauge,
  LayoutGrid,
  PiggyBank,
  Radio,
  Rocket,
  Sailboat,
  Settings,
  ShieldCheck,
  Snowflake,
  Sun,
  TrendingDown,
  Truck,
  Users,
  Waves,
  Wind,
  Wrench,
  Zap,
} from "lucide-react";

/* ---------- Custom SVGs (vessel-specific concepts lucide doesn't ship) --- */
// All inline SVGs use stroke="currentColor" + stroke-width 1.5 so they sit
// visually identical to lucide icons in the flyer's icon row and inherit
// the parent's Tailwind text colour for theming.

const svgProps = (className, strokeWidth, rest) => ({
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  className,
  "aria-hidden": "true",
  ...rest,
});

export const CraneIcon = ({ className = "", strokeWidth = 1.5, ...rest }) => (
  <svg {...svgProps(className, strokeWidth, rest)}>
    <path d="M6 21V5" />
    <path d="M6 5 L20 5" />
    <path d="M18 5 V11" />
    <path d="M16.5 11 H19.5 L19 13 H17 Z" />
    <path d="M3 21 H10" />
    <path d="M6 9 L9 12 M9 9 L6 12 M6 15 L9 18 M9 15 L6 18" />
  </svg>
);

export const WheelhouseIcon = ({ className = "", strokeWidth = 1.5, ...rest }) => (
  <svg {...svgProps(className, strokeWidth, rest)}>
    <path d="M4 9 L12 4 L20 9" />
    <path d="M5 9 V18 H19 V9" />
    <path d="M7 11 H17 V15 H7 Z" />
    <path d="M12 11 V15" />
    <path d="M15.5 18 V15.5" />
  </svg>
);

// Fishing net — diamond mesh pattern. Generic enough to cover seine, gill,
// and aquaculture mesh visually.
export const FishingNetIcon = ({ className = "", strokeWidth = 1.25, ...rest }) => (
  <svg {...svgProps(className, strokeWidth, rest)}>
    {/* Outer boundary — slightly trapezoidal so it reads as a hanging net */}
    <path d="M4 6 L20 6 L18 20 L6 20 Z" />
    {/* Diamond mesh — 3 rows of 4 crossing lines */}
    <path d="M8 6 L8 20 M12 6 L12 20 M16 6 L16 20" />
    <path d="M4 10 L20 10 M5 14 L19 14 M6 18 L18 18" />
    {/* Top rope */}
    <path d="M4 6 L20 6" strokeWidth={strokeWidth * 1.4} />
  </svg>
);

// Trawl net — cone-shaped net trailing behind a vessel. Distinct from the
// flat mesh icon so a trawler can pick "trawl-net" and an aquaculture
// vessel can pick "fishing-net" (the static mesh).
export const TrawlNetIcon = ({ className = "", strokeWidth = 1.25, ...rest }) => (
  <svg {...svgProps(className, strokeWidth, rest)}>
    {/* Tow point on the left (bridle) */}
    <path d="M3 12 L7 9 M3 12 L7 15" />
    {/* Cone outline — wide mouth on the left, narrow cod-end on the right */}
    <path d="M7 9 L20 11 L20 13 L7 15 Z" />
    {/* Mesh diagonals along the cone */}
    <path d="M9 10 L18 12 M11 10.4 L18 12 M9 14 L18 12 M11 13.6 L18 12" />
    {/* Cod-end weight/ball */}
    <circle cx="21" cy="12" r="1" />
  </svg>
);

// Aquaculture pen — circular fish pen viewed from above with mesh inside.
export const AquaPenIcon = ({ className = "", strokeWidth = 1.25, ...rest }) => (
  <svg {...svgProps(className, strokeWidth, rest)}>
    {/* Outer ring — floating collar */}
    <circle cx="12" cy="12" r="9" strokeWidth={strokeWidth * 1.4} />
    {/* Inner mesh — crossing diagonals representing the pen netting */}
    <path d="M5 12 L19 12 M12 5 L12 19" />
    <path d="M7 7 L17 17 M17 7 L7 17" />
    {/* Two small "fish" dots inside */}
    <circle cx="9.5" cy="9" r="0.6" fill="currentColor" />
    <circle cx="14.5" cy="14.5" r="0.6" fill="currentColor" />
  </svg>
);

// Winch — drum with cable wound around it. Visible side-on profile.
export const WinchIcon = ({ className = "", strokeWidth = 1.5, ...rest }) => (
  <svg {...svgProps(className, strokeWidth, rest)}>
    {/* Drum (a wide cylinder shown as two ellipses + sides) */}
    <ellipse cx="12" cy="9" rx="7" ry="2" />
    <path d="M5 9 V14" />
    <path d="M19 9 V14" />
    <ellipse cx="12" cy="14" rx="7" ry="2" />
    {/* Cable winding marks */}
    <path d="M6 11 H18 M6 12.5 H18" />
    {/* Cable hanging down with hook */}
    <path d="M12 16 V20" />
    <path d="M11 20 H13 L12.5 22 H11.5 Z" />
  </svg>
);

// RHIB / tender — small inflatable boat from the side.
export const RhibIcon = ({ className = "", strokeWidth = 1.5, ...rest }) => (
  <svg {...svgProps(className, strokeWidth, rest)}>
    {/* Hull bottom — shallow V */}
    <path d="M3 16 L7 19 L17 19 L21 16" />
    {/* Pontoon top */}
    <path d="M3 16 Q3 13 7 13 L17 13 Q21 13 21 16" />
    {/* Console + windshield */}
    <path d="M10 13 L10 9 L13 9 L13 13" />
    {/* Outboard motor at the stern */}
    <path d="M20 15 L22 15 L22 17 L20 17 Z" />
  </svg>
);

// Fender — cylindrical boat fender.
export const FenderIcon = ({ className = "", strokeWidth = 1.5, ...rest }) => (
  <svg {...svgProps(className, strokeWidth, rest)}>
    {/* Capsule (cylindrical fender) */}
    <rect x="9" y="3" width="6" height="18" rx="3" />
    {/* Three rope-attachment grooves */}
    <path d="M9 8 H15 M9 12 H15 M9 16 H15" />
    {/* Top eye */}
    <circle cx="12" cy="3" r="0.8" />
  </svg>
);

// Helideck — circle with H. Standard aviation helideck marking.
export const HelideckIcon = ({ className = "", strokeWidth = 1.5, ...rest }) => (
  <svg {...svgProps(className, strokeWidth, rest)}>
    <circle cx="12" cy="12" r="9" />
    {/* H letter */}
    <path d="M8 7 V17 M16 7 V17 M8 12 H16" strokeWidth={strokeWidth * 1.3} />
  </svg>
);

// Firefighting hose / monitor — water cannon.
export const FirefightingIcon = ({ className = "", strokeWidth = 1.5, ...rest }) => (
  <svg {...svgProps(className, strokeWidth, rest)}>
    {/* Pedestal / base */}
    <path d="M9 21 H15" />
    <path d="M11 21 V16" />
    <path d="M13 21 V16" />
    {/* Monitor / nozzle */}
    <path d="M9 14 H15 V16 H9 Z" />
    <path d="M15 15 L21 13 V17 Z" />
    {/* Water jet droplets */}
    <path d="M21 11 V8" />
    <path d="M22 14 L23 11" />
    <path d="M20 16 L22 18" />
  </svg>
);

// Net (alias for FishingNet) — kept as a generic short key.
export const NetIcon = FishingNetIcon;

/* ---------- Catalogue ---------------------------------------------------- */

export const FEATURE_ICONS = [
  // Vessel-specific custom SVGs first
  { key: "crane",        label: "Crane / davit",          Icon: CraneIcon,
    keywords: /\bcrane(s)?\b|\bdavit(s)?\b|lifting.?gear|hiab|knuckle.?boom/i },
  { key: "wheelhouse",   label: "Wheelhouse / bridge",    Icon: WheelhouseIcon,
    keywords: /wheelhouse|pilot.?house|\bbridge.?(?:deck|cabin|house)\b|helm.?station/i },
  { key: "winch",        label: "Winch",                  Icon: WinchIcon,
    keywords: /\bwinch(es)?\b|capstan|windlass/i },
  { key: "fishing-net",  label: "Fishing / seine net",    Icon: FishingNetIcon,
    keywords: /\bseine\b|\bgill.?net\b|\bnet(s|ting)?\b(?!.work)|aquaculture.?net|mesh/i },
  { key: "trawl-net",    label: "Trawl net",              Icon: TrawlNetIcon,
    keywords: /trawl(er|ing)?|drag.?net|cod.?end/i },
  { key: "aqua-pen",     label: "Aquaculture pen",        Icon: AquaPenIcon,
    keywords: /pen(s)?\b|fish.?farm|salmon|smolt|aquaculture/i },
  { key: "rhib",         label: "RHIB / tender",          Icon: RhibIcon,
    keywords: /\brhib\b|\btender\b|inflatable|launch.?boat/i },
  { key: "fender",       label: "Fender",                 Icon: FenderIcon,
    keywords: /\bfender(s|ing)?\b|bow.?fender/i },
  { key: "helideck",     label: "Helideck",               Icon: HelideckIcon,
    keywords: /helideck|helipad|helicopter/i },
  { key: "firefighting", label: "Firefighting / monitor", Icon: FirefightingIcon,
    keywords: /fire.?fight|fi.?fi|fire.?monitor|water.?cannon/i },

  // Generic marine/operational icons (lucide)
  { key: "waves",         label: "Hull / sea-keeping",   Icon: Waves,
    keywords: /catamaran|wave.?pierc|hull|sea.?keep|seakeep/i },
  { key: "gauge",         label: "Engine / speed",       Icon: Gauge,
    keywords: /outboard|engine|propuls|knot|\bspeed|fast\b|\bpower/i },
  { key: "door",          label: "Boarding / access",    Icon: DoorOpen,
    keywords: /boarding|gate|door(?!ston)|\baccess\b|entry/i },
  { key: "boxes",         label: "Cargo / payload",      Icon: Boxes,
    keywords: /aft.?deck|cargo|deck.?area|payload|load|stow|hold\b|container|pallet|tote/i },
  { key: "users",         label: "Crew / passengers",    Icon: Users,
    keywords: /personnel|passenger|crew|seat|cabin|comfort/i },
  { key: "wrench",        label: "Maintenance",          Icon: Wrench,
    keywords: /maintenan|reliab|service|uptime/i },
  { key: "fuel",          label: "Fuel / range",         Icon: Fuel,
    keywords: /\bfuel\b|diesel|petrol|bunker|kerosene|\bMPG\b|litres?.?per|gal(?:lons?)?.?per|fuel.?tank|fuel.?range|fuel.?burn|fuel.?consumpt/i },
  { key: "savings",       label: "Savings / efficiency", Icon: PiggyBank,
    keywords: /saving|cheap|afford|budget|price|efficien/i },
  { key: "shield",        label: "Safety / protection",  Icon: ShieldCheck,
    keywords: /safe|safety|secur|protect|shield/i },
  { key: "compass",       label: "Navigation / GPS",     Icon: Compass,
    keywords: /navig|gps|chart|compass|radar/i },
  { key: "radio",         label: "Comms / radio",        Icon: Radio,
    keywords: /comm|radio|antenna|comms|comm[s]?/i },
  { key: "anchor",        label: "Anchor / mooring",     Icon: Anchor,
    keywords: /anchor|moor/i },
  { key: "layout",        label: "Versatile / modular",  Icon: LayoutGrid,
    keywords: /version|versat|flexib|modul/i },
  { key: "zap",           label: "Electrical / hybrid",  Icon: Zap,
    keywords: /electr|battery|hybrid|solar|charg/i },
  { key: "wind",          label: "Wind / ventilation",   Icon: Wind,
    keywords: /wind|aero|ventilat/i },
  { key: "snowflake",     label: "Cold / refrigeration", Icon: Snowflake,
    keywords: /cold|ice|reefer|chill|frozen/i },
  { key: "sun",           label: "Heat / tropics",       Icon: Sun,
    keywords: /heat|warm|sun|tropic/i },
  { key: "truck",         label: "Tow / transport",      Icon: Truck,
    keywords: /tow|haul|truck|transport|deliver/i },
  { key: "dollar",        label: "Cost / value",         Icon: DollarSign,
    keywords: /cost|value|price.?point|low.?operating|operating.?cost/i },
  { key: "building",      label: "Offshore / construction", Icon: Building2,
    keywords: /offshore|wind.?farm|construction|infrastructure/i },
  { key: "cog",           label: "Configurable / setup", Icon: Cog,
    keywords: /configur|setup|custom|fit.?out/i },
  { key: "columns",       label: "Capacity / space",     Icon: Columns3,
    keywords: /spacious|capacity|size|area|columns?|layout/i },
  { key: "settings",      label: "Durable / robust",     Icon: Settings,
    keywords: /build|construct|durab|robust|tough/i },
  { key: "rocket",        label: "Launch / acceleration", Icon: Rocket,
    keywords: /launch|rocket|burst|accel/i },
  { key: "trending-down", label: "Economy / lower",      Icon: TrendingDown,
    keywords: /economy|lower|reduce|down/i },
  { key: "sailboat",      label: "Sail / yacht",         Icon: Sailboat,
    keywords: /sail|yacht/i },
];

/** Map icon-key → React component. Unknown keys return ``null`` so callers
 *  can fall back to the auto-picker. */
export function getIconByKey(key) {
  if (!key) return null;
  const entry = FEATURE_ICONS.find((c) => c.key === key);
  return entry ? entry.Icon : null;
}

/** Find a catalogue entry by key (returns ``undefined`` if unknown). */
export function getIconEntry(key) {
  return FEATURE_ICONS.find((c) => c.key === key);
}

/* ---------- Auto-picker -------------------------------------------------- */
//
// Walks the catalogue in order for each feature, picking the first rule whose
// keywords match AND whose icon hasn't already been used in this list. If a
// feature has ``icon_key`` set (admin override) that takes absolute priority
// and the icon is excluded from the dedup pool.

const FALLBACK_KEYS = ["shield", "layout", "dollar", "wrench", "columns"];

/** Icons that must NEVER be paired with certain feature texts even if the
 *  keyword would otherwise match — guards against the dedup fallback
 *  leaking a wrong-icon onto cargo / crew rows. */
const ICON_EXCLUSIONS = [
  // Cargo / payload / hold features must NEVER show the fuel icon — even if
  // the description happens to mention generic "tank" / "range" / "consumption".
  { textPattern: /cargo|payload|hold\b|stow|deck.?area|aft.?deck/i, blocked: ["fuel"] },
  // Passenger / crew features should never show the fuel icon either.
  { textPattern: /passenger|crew|personnel|seat|cabin/i, blocked: ["fuel"] },
];

export function pickFeatureIcons(features) {
  const used = new Set();
  return (features || []).map((feature, idx) => {
    // 0) Manual override wins absolutely.
    if (feature?.icon_key) {
      const entry = getIconEntry(feature.icon_key);
      if (entry) {
        used.add(entry.key);
        return entry.Icon;
      }
    }

    const text = `${feature?.title || ""} ${feature?.description || ""}`.toLowerCase();
    const excluded = new Set();
    for (const { textPattern, blocked } of ICON_EXCLUSIONS) {
      if (textPattern.test(text)) blocked.forEach((k) => excluded.add(k));
    }

    let firstMatch = null;
    for (const entry of FEATURE_ICONS) {
      if (!entry.keywords) continue;
      if (!entry.keywords.test(text)) continue;
      if (excluded.has(entry.key)) continue;
      if (firstMatch == null) firstMatch = entry;
      if (!used.has(entry.key)) {
        used.add(entry.key);
        return entry.Icon;
      }
    }

    for (let i = 0; i < FALLBACK_KEYS.length; i++) {
      const k = FALLBACK_KEYS[(idx + i) % FALLBACK_KEYS.length];
      if (excluded.has(k)) continue;
      if (!used.has(k)) {
        used.add(k);
        return getIconByKey(k);
      }
    }

    return (firstMatch && firstMatch.Icon) || getIconByKey(FALLBACK_KEYS[idx % FALLBACK_KEYS.length]);
  });
}
